import React, { useContext, useEffect, useMemo, useState } from "react";
import { ProfileCardSection } from "./ProfileCardSection";
import { renderTextWithTitleAndChildComponent } from "../../../components/TextWithHeader";
import { CircularProgress, Divider, IconButton, Switch } from "@material-ui/core";
import { RBButton, RBButtonType } from "../../../components/RBButton";
import { RBContext } from "../../../components/RBContext";
import { Colors, FontSize, Spacing } from "../../../theme/Theme";
import { AuthService } from "../../../api/AuthService";
import { PaymentService } from "../../../api/PaymentService";
import { Refresh } from "@material-ui/icons";
import { RBModal } from "../../../components/RBModal";
import { RBCardView } from "../../../components/RBCardView";
import { renderRBPayText } from "../../../components/RBPayWordmark";
import { useStripePaymentRefresh } from "../../../util/useStripePaymentRefresh";
import { RBAlert } from "../../../components/RBAlert";
import { useHistory } from "react-router-dom";
import { RoutePathConstant, roleNameEnum } from "../../../util/Common";
import {
  isRBPayActiveStatus,
  isRBPayAvailableForWorker,
  isRBPayEnabledForAccount,
  RB_PAY_PAYMENT_METHOD,
} from "../../../util/rbPay";
import { rbPaySettingsService } from "../../rbPaySettings/services/rbPaySettings.service";

interface AcceptablePaymentMethodsSectionProps {
  accountInfo?: any;
  onAccountUpdated?: (accountInfo: any) => void;
}

const formatPaymentMethodLabel = (value: string) => {
  const normalized = String(value || "").trim().toLowerCase();
  switch (normalized) {
    case "direct_deposit":
      return "Direct Deposit";
    case "emt":
      return "EMT";
    case "cheque":
      return "Cheque";
    case "stripe":
      return "Stripe";
    case "rb_pay":
      return "RB Pay";
    case "other":
      return "Other";
    default:
      return value;
  }
};

/**
 * Checks if the network pays workers (networkPays === true).
 * When true, Stripe is excluded and the view is read-only.
 */
const getIsNetworkPaid = (accountInfo: any): boolean => {
  return accountInfo?.networks?.[0]?.paymentConfig?.networkPays === true;
};

const getAllowedPaymentMethods = (accountInfo: any, isNetworkPaid: boolean): string[] => {
  const allowed = accountInfo?.networks?.[0]?.paymentConfig?.allowedWorkerPaymentMethods;
  if (!Array.isArray(allowed)) {
    return [];
  }
  // When network pays, filter out Stripe (same as mobile logic)
  if (isNetworkPaid) {
    return allowed.filter((method: string) => !isStripeMethod(method));
  }
  return allowed;
};

const getSelectedPaymentMethods = (accountInfo: any, allowedMethods: string[]): string[] => {
  const selected = accountInfo?.pharmacist?.acceptedPaymentMethods;
  if (!Array.isArray(selected)) {
    return [];
  }
  // Filter selected methods by allowed methods (case-insensitive for stripe)
  return selected.filter((method: string) => {
    // For stripe, do case-insensitive comparison
    if (isStripeMethod(method)) {
      return allowedMethods.some((allowed) => isStripeMethod(allowed));
    }
    // For other methods, check exact match or case-insensitive
    return allowedMethods.some(
      (allowed) => allowed.toLowerCase() === method.toLowerCase()
    );
  });
};

const isStripeMethod = (method: string) => String(method || "").trim().toLowerCase() === "stripe";

/**
 * Gets the actual stripe method key from allowedMethods (preserves original casing from backend).
 * Mirrors mobile's getStripeMethod().
 */
const getStripeMethodKey = (allowedMethods: string[]): string | null => {
  return allowedMethods.find((method) => isStripeMethod(method)) || null;
};

/**
 * Gets direct (non-Stripe) payment methods.
 * Uses new backend fields:
 * - Worker level: pharmacist.otherPaymentMethods (copy of network config)
 * - Network level: networks[0].paymentConfig.defaultWorkerPaymentMethods
 */
const getDirectPaymentMethods = (accountInfo: any): string[] => {
  // First try worker-level otherPaymentMethods (convenience copy from backend)
  const workerOtherMethods = accountInfo?.pharmacist?.otherPaymentMethods;
  if (Array.isArray(workerOtherMethods) && workerOtherMethods.length > 0) {
    return workerOtherMethods;
  }
  // Fallback to network-level defaultWorkerPaymentMethods
  const networkDefaultMethods = accountInfo?.networks?.[0]?.paymentConfig?.defaultWorkerPaymentMethods;
  if (Array.isArray(networkDefaultMethods)) {
    return networkDefaultMethods;
  }
  return [];
};

const buildSelectedPaymentMethods = (
  accountInfo: any,
  allowedMethods: string[],
  selectedMethods: string[],
  isNetworkPaid: boolean
): string[] => {
  const directMethods = getDirectPaymentMethods(accountInfo);
  // When network pays, only return direct methods (no Stripe)
  if (isNetworkPaid) {
    return directMethods;
  }
  const stripeMethodKey = getStripeMethodKey(allowedMethods);
  const stripeAllowed = !!stripeMethodKey;
  const stripeSelected = selectedMethods.some((method) => isStripeMethod(method));

  // Use the actual stripe method key from allowedMethods to preserve casing
  if (stripeAllowed && stripeSelected && stripeMethodKey) {
    return [...directMethods, stripeMethodKey];
  }
  return [...directMethods];
};

/**
 * Returns the title for the payment methods section based on networkPays flag.
 * Mirrors mobile's getDefaultMethodsTitle().
 */
const getDefaultMethodsTitle = (isNetworkPaid: boolean): string => {
  return isNetworkPaid
    ? "Accept Payment Via:"
    : "Offices pay the workers directly via:";
};

export const AcceptablePaymentMethodsSection: React.FC<AcceptablePaymentMethodsSectionProps> = ({
  accountInfo,
  onAccountUpdated,
}) => {
  const history = useHistory();
  const { industryMode, primaryColor, secondaryColor } = useContext(RBContext);

  // Determine if network pays (read-only mode)
  const isNetworkPaid = useMemo(() => getIsNetworkPaid(accountInfo), [accountInfo]);

  const [isEdit, setIsEdit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOpeningStripe, setIsOpeningStripe] = useState(false);
  const [isRefreshingStripeStatus, setIsRefreshingStripeStatus] = useState(false);
  const [showStripeHowTo, setShowStripeHowTo] = useState(false);
  const [allowedPaymentMethods, setAllowedPaymentMethods] = useState<string[]>([]);
  const [initialSelectedPaymentMethods, setInitialSelectedPaymentMethods] = useState<string[]>([]);
  const [selectedPaymentMethods, setSelectedPaymentMethods] = useState<string[]>([]);
  const [connectAccountStatus, setConnectAccountStatus] = useState<string | null>(
    accountInfo?.pharmacist?.connectAccountStatus || null
  );
  const [stripeConnectId, setStripeConnectId] = useState<string | null>(
    accountInfo?.pharmacist?.stripeConnectId || null
  );
  const [stripeSetupDone, setStripeSetupDone] = useState<boolean>(
    !!accountInfo?.pharmacist?.flags?.isBankingEnabled
  );
  const [stripeSetupRefreshTrigger, setStripeSetupRefreshTrigger] = useState(0);
  const [rbPaySettings, setRbPaySettings] = useState<any>(null);
  const [isRBPayLoading, setIsRBPayLoading] = useState(false);
  // RB Pay is staged like Stripe: the toggle updates this local selection only and
  // is persisted by the Save button (as "rb_pay" in acceptedPaymentMethods).
  // RB Pay can only be ENABLED once its status is Active. So the toggle must NOT
  // default on for a pending / not-started worker even if "rb_pay" is already in
  // acceptedPaymentMethods (the BE adds it at setup submission). Gate by status.
  const [isRBPaySelected, setIsRBPaySelected] = useState<boolean>(
    isRBPayActiveStatus(accountInfo) && isRBPayEnabledForAccount(accountInfo)
  );
  const [initialRBPaySelected, setInitialRBPaySelected] = useState<boolean>(
    isRBPayActiveStatus(accountInfo) && isRBPayEnabledForAccount(accountInfo)
  );

  // Alert modal state
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const showAlert = (message: string) => {
    setAlertMessage(message);
    setAlertVisible(true);
  };

  // Hook for auto-refresh when user returns from Stripe setup
  const { startStripePaymentWatch, stopStripePaymentWatch } = useStripePaymentRefresh({
    onRefresh: () => {
      console.log("AcceptablePaymentMethodsSection: onRefresh callback invoked, incrementing trigger");
      setStripeSetupRefreshTrigger(prev => prev + 1);
    },
  });

  const directPaymentMethods = useMemo(
    () => getDirectPaymentMethods(accountInfo),
    [accountInfo]
  );
  const shouldShowRBPay = useMemo(
    () =>
      isRBPayAvailableForWorker({
        industryMode,
        roleName: roleNameEnum.pharmacist,
        account: accountInfo,
      }),
    [accountInfo, industryMode]
  );
  const isStripeAllowed = useMemo(
    () => !isNetworkPaid && allowedPaymentMethods.some((method) => isStripeMethod(method)),
    [allowedPaymentMethods, isNetworkPaid]
  );
  const isStripeSelected = useMemo(
    () => selectedPaymentMethods.some((method) => isStripeMethod(method)),
    [selectedPaymentMethods]
  );
  const wasStripeSelectedInitially = useMemo(
    () => initialSelectedPaymentMethods.some((method) => isStripeMethod(method)),
    [initialSelectedPaymentMethods]
  );

  useEffect(() => {
    const networkPaid = getIsNetworkPaid(accountInfo);
    const allowedMethods = getAllowedPaymentMethods(accountInfo, networkPaid);
    const currentMethods = getSelectedPaymentMethods(accountInfo, allowedMethods);
    const normalizedSelectedMethods = buildSelectedPaymentMethods(accountInfo, allowedMethods, currentMethods, networkPaid);
    setAllowedPaymentMethods(allowedMethods);
    setInitialSelectedPaymentMethods(normalizedSelectedMethods);
    setSelectedPaymentMethods(normalizedSelectedMethods);
    setConnectAccountStatus(accountInfo?.pharmacist?.connectAccountStatus || null);
    setStripeConnectId(accountInfo?.pharmacist?.stripeConnectId || null);
    setStripeSetupDone(!!accountInfo?.pharmacist?.flags?.isBankingEnabled);
    const rbPayOn = isRBPayActiveStatus(accountInfo) && isRBPayEnabledForAccount(accountInfo);
    setIsRBPaySelected(rbPayOn);
    setInitialRBPaySelected(rbPayOn);
  }, [accountInfo]);

  useEffect(() => {
    let isMounted = true;

    const loadRBPaySettings = async () => {
      if (!shouldShowRBPay || !accountInfo) {
        if (isMounted) {
          setRbPaySettings(null);
        }
        return;
      }

      setIsRBPayLoading(true);

      try {
        const nextSettings = await rbPaySettingsService.getWorkerRBPaySettings({
          account: accountInfo,
          industryMode,
        });

        if (isMounted) {
          setRbPaySettings(nextSettings);
          // Once the authoritative status loads, force the toggle off if RB Pay
          // isn't Active — covers a stale/empty account.rbPayStatus that let the
          // initial state seed it on. (Never force on; an Active worker keeps the
          // accepted-methods value.)
          const resolvedStatus = String(nextSettings?.approvalInfo?.status || "")
            .trim()
            .toLowerCase();
          if (resolvedStatus !== "active") {
            setIsRBPaySelected(false);
            setInitialRBPaySelected(false);
          }
        }
      } catch (error) {
        if (isMounted) {
          setRbPaySettings(null);
        }
      } finally {
        if (isMounted) {
          setIsRBPayLoading(false);
        }
      }
    };

    loadRBPaySettings();

    const handleRBPayUpdate = () => {
      loadRBPaySettings();
    };

    window.addEventListener("rb-pay-settings-updated", handleRBPayUpdate);

    return () => {
      isMounted = false;
      window.removeEventListener("rb-pay-settings-updated", handleRBPayUpdate);
    };
  }, [accountInfo, industryMode, shouldShowRBPay]);

  // Auto-refresh Stripe status when user returns from Stripe setup
  useEffect(() => {
    if (stripeSetupRefreshTrigger > 0) {
      console.log("AcceptablePaymentMethodsSection: Stripe setup refresh triggered");
      refreshStripeStatus(true);
    }
  }, [stripeSetupRefreshTrigger]);

  const getConnectStatus = () => String(connectAccountStatus || "").trim().toLowerCase();

  const isStripeSetupDone = () => !!stripeSetupDone;

  const isStripeRestricted = () => getConnectStatus() === "restricted";

  const isStripeActive = () => getConnectStatus() === "active";

  const shouldShowStripeStatusCheckRow = () => {
    const status = getConnectStatus();
    return status !== "" && status !== "onboarding" && status !== "active";
  };

  const canEnableStripeMethod = () => {
    const status = getConnectStatus();
    return !!stripeConnectId && (status === "active" || status === "restricted");
  };

  const getRBPayDisplayName = () => "RB Pay";

  const getRBPayStatus = () => {
    const normalizedStatus = String(
      rbPaySettings?.approvalInfo?.status || accountInfo?.pharmacist?.rbPayStatus || ""
    )
      .trim()
      .toLowerCase();

    if (normalizedStatus === "active") {
      return "active";
    }

    if (normalizedStatus === "pending" || normalizedStatus === "in review" || normalizedStatus === "in-review") {
      return "pending";
    }

    return "not-started";
  };

  const getRBPayStatusLabel = () => {
    const status = getRBPayStatus();

    if (status === "active") {
      return "Active";
    }

    if (status === "pending") {
      return "Pending";
    }

    return "";
  };

  const canEnableRBPay = () => getRBPayStatus() === "active";

  // Reflects the staged selection (like the Stripe switch), persisted on Save.
  const isRBPayEnabled = () => isRBPaySelected;

  const getRBPayHelperText = () => {
    if (getRBPayStatus() === "pending") {
      return `You can enable ${getRBPayDisplayName()} once the ${getRBPayDisplayName()} Status is Active.`;
    }

    return `You can enable ${getRBPayDisplayName()} once you complete the ${getRBPayDisplayName()} Setup.`;
  };

  const openRBPaySettings = () => {
    history.push(RoutePathConstant.RBPaySettings);
  };

  // Instant, no API — just stages the selection (like the Stripe switch). The Save
  // button persists it as "rb_pay" in acceptedPaymentMethods.
  const onToggleRBPay = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked && !canEnableRBPay()) {
      showAlert(
        `${getRBPayDisplayName()} can only be enabled when the ${getRBPayDisplayName()} Status is Active.`
      );
      return;
    }
    setIsRBPaySelected(checked);
  };

  const hasSelectionChanged = () => {
    // RB Pay is the worker's own opt-in (rides in acceptedPaymentMethods like
    // Stripe) and is saveable even in network-paid mode, so a change to it must
    // enable Save regardless of who pays.
    if (isRBPaySelected !== initialRBPaySelected) {
      return true;
    }
    // The network-configured (direct / Stripe) methods are read-only when the
    // network pays, so nothing else can change in that mode.
    if (isNetworkPaid) {
      return false;
    }
    return wasStripeSelectedInitially !== isStripeSelected;
  };

  const hasAtLeastOnePaymentMethodSelected = () => {
    const nextSelectedMethods = buildSelectedPaymentMethods(
      accountInfo,
      allowedPaymentMethods,
      selectedPaymentMethods,
      isNetworkPaid
    );
    return allowedPaymentMethods.length === 0 || nextSelectedMethods.length > 0;
  };

  const getStripeStatusLabel = () => {
    const status = String(connectAccountStatus || "").trim();
    if (!status) {
      return "Not Active";
    }
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const shouldShowStripeHowTo = () => {
    const status = getConnectStatus();
    return status === "" || status === "onboarding";
  };

  const openExternal = (url: string) => {
    // Note: window.open with "noopener,noreferrer" returns null by design (security feature)
    // We can't reliably detect if the window opened, so we just attempt to open it
    // If popup is blocked, user will see browser's popup blocked notification
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const refreshStripeStatus = async (silent = false) => {
    if (isRefreshingStripeStatus) {
      return;
    }
    try {
      setIsRefreshingStripeStatus(true);
      const latestAccount = await AuthService.loadAccountInfo(); //valid call.
      setConnectAccountStatus(latestAccount?.pharmacist?.connectAccountStatus || null);
      setStripeConnectId(latestAccount?.pharmacist?.stripeConnectId || null);
      setStripeSetupDone(!!latestAccount?.pharmacist?.flags?.isBankingEnabled);
      onAccountUpdated?.(latestAccount);
    } catch (error) {
      if (!silent) {
        showAlert("Unable to refresh Stripe status. Please try again.");
      }
    } finally {
      setIsRefreshingStripeStatus(false);
    }
  };

  const openStripeDestination = async (closeHowToModalOnOpen = false) => {
    if (isOpeningStripe) {
      return;
    }
    setIsOpeningStripe(true);
    try {
      if (isStripeActive() || shouldShowStripeStatusCheckRow() || isStripeSetupDone()) {
        const response = await PaymentService.getStripeConnectLoginLink();
        const loginLink = response?.loginLink;
        if (!loginLink) {
          throw new Error("Stripe login link was not returned. Please try again.");
        }
        console.log("AcceptablePaymentMethodsSection: Opening Stripe login, starting watch...");
        // Start watching BEFORE opening external link
        startStripePaymentWatch();
        openExternal(loginLink);
        if (closeHowToModalOnOpen) {
          setShowStripeHowTo(false);
        }
        return;
      }

      const response = await PaymentService.registerWorkerStripeAccount();
      const onboardingLink = response?.accountLink;
      if (!onboardingLink) {
        throw new Error("Stripe setup link was not returned. Please try again.");
      }
      console.log("AcceptablePaymentMethodsSection: Opening Stripe onboarding, starting watch...");
      // Start watching BEFORE opening external link
      startStripePaymentWatch();
      openExternal(onboardingLink);
      if (closeHowToModalOnOpen) {
        setShowStripeHowTo(false);
      }
    } catch (error) {
      const err: any = error;
      const message =
        err?.response?.data?.error || err?.message || "Unable to start Stripe setup. Please try again.";
      showAlert(message);
    } finally {
      setIsOpeningStripe(false);
    }
  };

  const onToggle = (methodKey: string) => {
    if (!isStripeMethod(methodKey)) {
      return;
    }

    if (!isStripeSelected && !canEnableStripeMethod()) {
      showAlert(
        "Stripe can be enabled only when Stripe account is connected and status is Active or Restricted."
      );
      return;
    }

    setSelectedPaymentMethods((prev) => {
      const directMethods = getDirectPaymentMethods(accountInfo);
      // Use actual stripe method key from allowedPaymentMethods to preserve casing
      const stripeKey = getStripeMethodKey(allowedPaymentMethods) || "stripe";
      return isStripeSelected ? directMethods : [...directMethods, stripeKey];
    });
  };

  const handleSave = async () => {
    // Note: the network-configured (direct / Stripe) methods are read-only in
    // network-paid mode, but the worker's RB Pay opt-in is still saveable — it
    // rides in the same acceptedPaymentMethods array and is persisted below.
    if (!hasAtLeastOnePaymentMethodSelected()) {
      showAlert("At least 1 payment method must be selected.");
      return;
    }

    setIsSaving(true);
    try {
      const methodsToSave = buildSelectedPaymentMethods(
        accountInfo,
        allowedPaymentMethods,
        selectedPaymentMethods,
        isNetworkPaid
      );
      // RB Pay rides in the same acceptedPaymentMethods array as Stripe. Persist the
      // staged RB Pay selection together with the Stripe/direct methods in one save.
      const finalMethodsToSave = isRBPaySelected
        ? [...methodsToSave, RB_PAY_PAYMENT_METHOD]
        : methodsToSave;
      await AuthService.updateWorker({
        acceptedPaymentMethods: finalMethodsToSave,
      });

      const latestAccount = await AuthService.loadAccountInfo(); //valid call.
      const networkPaid = getIsNetworkPaid(latestAccount);
      const allowedMethods = getAllowedPaymentMethods(latestAccount, networkPaid);
      const currentMethods = getSelectedPaymentMethods(latestAccount, allowedMethods);
      const normalizedSelectedMethods = buildSelectedPaymentMethods(latestAccount, allowedMethods, currentMethods, networkPaid);

      setAllowedPaymentMethods(allowedMethods);
      setInitialSelectedPaymentMethods(normalizedSelectedMethods);
      setSelectedPaymentMethods(normalizedSelectedMethods);
      setConnectAccountStatus(latestAccount?.pharmacist?.connectAccountStatus || null);
      setStripeConnectId(latestAccount?.pharmacist?.stripeConnectId || null);
      setStripeSetupDone(!!latestAccount?.pharmacist?.flags?.isBankingEnabled);
      setIsRBPaySelected(isRBPaySelected);
      setInitialRBPaySelected(isRBPaySelected);

      onAccountUpdated?.(latestAccount);
      setIsEdit(false);
    } catch (error) {
      const err: any = error;
      const message =
        err?.response?.data?.error ||
        "Unable to update payment availability. Please try again.";
      showAlert(message);
    } finally {
      setIsSaving(false);
    }
  };

  const enabledMethodLabels = [
    ...selectedPaymentMethods.map((method) => formatPaymentMethodLabel(method)),
    // RB Pay isn't part of selectedPaymentMethods (it's not in the network's
    // allowed list), so surface it explicitly when enabled.
    ...(shouldShowRBPay && isRBPaySelected ? [formatPaymentMethodLabel(RB_PAY_PAYMENT_METHOD)] : []),
  ];

  const viewDetail = renderTextWithTitleAndChildComponent(
    "",
    <div style={{ width: "100%" }}>
      {enabledMethodLabels.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
          {enabledMethodLabels.map((label, index) => (
            <div
              key={`${label}-${index}`}
              style={{
                display: "inline-block",
                fontSize: FontSize.default,
                fontWeight: "bold",
                color: Colors.black,
                borderColor: Colors.defaultGrey,
                backgroundColor: Colors.defaultGrey,
                borderRadius: 10,
                borderWidth: 1,
                padding: Spacing.smallest,
                marginRight: Spacing.small,
                marginBottom: Spacing.smallest,
              }}
            >
              {label}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ color: Colors.darkGrey }}>
          No payment methods selected.
        </div>
      )}
    </div>
  );

  const editDetail = renderTextWithTitleAndChildComponent(
    "",
    <div style={{ width: "100%" }}>
      <div style={{ color: Colors.darkGrey, fontWeight: "bold", marginBottom: Spacing.small }}>
        Offices pay the workers directly via:
      </div>
      {directPaymentMethods.length > 0 ? (
        <ul style={{ marginTop: 0, marginBottom: Spacing.medium, paddingLeft: 22, color: Colors.black }}>
          {directPaymentMethods.map((method) => (
            <li key={method}>{formatPaymentMethodLabel(method)}</li>
          ))}
        </ul>
      ) : (
        <div style={{ color: Colors.darkGrey, marginBottom: Spacing.medium }}>
          No direct payment methods configured by your network.
        </div>
      )}

      {isStripeAllowed && (
        <div
          style={{
            color: Colors.darkGrey,
            fontSize: FontSize.small,
            marginBottom: Spacing.medium,
            backgroundColor: Colors.defaultGrey,
            borderRadius: 8,
            padding: Spacing.small,
          }}
        >
          <div>
            Optional: Select and setup your digital payment methods facilitated through Relief
            Buddy's payment partner platforms.
          </div>
          <div style={{ marginTop: Spacing.smallest }}>
            Digital Payments are from the Office directly to the worker, as an Independent
            Contractor.
          </div>
        </div>
      )}

      <div style={{ marginTop: Spacing.large }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            color: Colors.darkGrey,
            fontWeight: "bold",
            marginBottom: Spacing.small,
          }}
        >
          <div>Method</div>
          <div>Enabled</div>
        </div>
        <Divider style={{ marginBottom: Spacing.small }} />

        {!isStripeAllowed && (
          <div style={{ color: Colors.darkGrey, marginBottom: Spacing.small }}>
            No optional digital payment methods are currently available.
          </div>
        )}

        {isStripeAllowed && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontWeight: "bold", fontSize: FontSize.large }}>
                Stripe (Independent Contractor Only)
              </div>
              <Switch
                checked={isStripeSelected}
                onChange={() => onToggle("stripe")}
                color="primary"
                disabled={isSaving || (!isStripeSelected && !canEnableStripeMethod())}
              />
            </div>

            <div style={{ marginBottom: Spacing.small }}>
              {isStripeActive() ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div
                    style={{
                      color: Colors.themeGreen,
                      fontWeight: "bold",
                      fontSize: FontSize.default,
                    }}
                  >
                    Active
                  </div>
                  <RBButton
                    text="Open Stripe"
                    onClick={() => {
                      if (!isOpeningStripe) {
                        openStripeDestination();
                      }
                    }}
                    buttonBackgroundColor={secondaryColor}
                    isLoading={isOpeningStripe}
                    disabled={isOpeningStripe}
                  />
                </div>
              ) : shouldShowStripeStatusCheckRow() ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <div
                    style={{
                      color: Colors.red,
                      fontWeight: "bold",
                      fontSize: FontSize.default,
                    }}
                  >
                    {getStripeStatusLabel()}
                  </div>
                  <IconButton
                    size="small"
                    onClick={() => refreshStripeStatus(false)}
                    disabled={isRefreshingStripeStatus}
                  >
                    {isRefreshingStripeStatus ? (
                      <CircularProgress size={14} />
                    ) : (
                      <Refresh style={{ fontSize: 16, color: secondaryColor || Colors.themeLightBlue }} />
                    )}
                  </IconButton>
                  <span style={{ color: Colors.darkGrey }}>•</span>
                  <span
                    style={{
                      color: secondaryColor || Colors.themeLightBlue,
                      fontWeight: "bold",
                      cursor: isOpeningStripe ? "default" : "pointer",
                      opacity: isOpeningStripe ? 0.7 : 1,
                    }}
                    onClick={() => {
                      if (!isOpeningStripe) {
                        openStripeDestination();
                      }
                    }}
                  >
                    {isOpeningStripe ? "Opening Stripe..." : "Check Status in Stripe"}
                  </span>
                </div>
              ) : (
                <div style={{ marginTop: Spacing.smallest }}>
                  <RBButton
                    text="Open Stripe"
                    onClick={() => {
                      if (!isOpeningStripe) {
                        openStripeDestination();
                      }
                    }}
                    buttonBackgroundColor={secondaryColor}
                    isLoading={isOpeningStripe}
                    disabled={isOpeningStripe}
                  />
                </div>
              )}

              {!isStripeActive() && shouldShowStripeHowTo() && (
                <div style={{ marginTop: Spacing.smallest }}>
                  <span
                    style={{
                      color: secondaryColor || Colors.themeLightBlue,
                      fontWeight: "bold",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => setShowStripeHowTo(true)}
                  >
                    How to Setup
                  </span>
                </div>
              )}

              {!isStripeSelected && !canEnableStripeMethod() && (
                <div
                  style={{
                    marginTop: Spacing.smallest,
                    color: Colors.darkGrey,
                    fontSize: FontSize.default,
                    fontStyle: "italic",
                  }}
                >
                  You can enable Stripe once you complete the Stripe setup.
                </div>
              )}
            </div>
          </div>
        )}

        {shouldShowRBPay && (
          <div style={{ marginTop: isStripeAllowed ? Spacing.large : 0 }}>
            <Divider style={{ marginBottom: Spacing.small }} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: Spacing.medium,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: "bold", fontSize: FontSize.large }}>{renderRBPayText(getRBPayDisplayName())}</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    marginTop: Spacing.smallest,
                  }}
                >
                  {getRBPayStatusLabel() ? (
                    <div
                      style={{
                        color: getRBPayStatus() === "active" ? Colors.themeGreen : "#ef7a2f",
                        fontWeight: "bold",
                        fontSize: FontSize.default,
                      }}
                    >
                      {getRBPayStatusLabel()}
                    </div>
                  ) : null}
                  <RBButton
                    text={`Open ${getRBPayDisplayName()}`}
                    onClick={openRBPaySettings}
                    buttonBackgroundColor={secondaryColor}
                    disabled={isRBPayLoading}
                  />
                </div>

                {!canEnableRBPay() && !isRBPayEnabled() ? (
                  <div
                    style={{
                      marginTop: Spacing.smallest,
                      color: Colors.darkGrey,
                      fontSize: FontSize.default,
                      fontStyle: "italic",
                    }}
                  >
                    {renderRBPayText(getRBPayHelperText())}
                  </div>
                ) : null}
              </div>

              <Switch
                checked={isRBPayEnabled()}
                onChange={onToggleRBPay}
                color="primary"
                disabled={isRBPayLoading || (!isRBPayEnabled() && !canEnableRBPay())}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const action = (
    <RBButton
      text={isNetworkPaid ? "View Payments" : "Edit Payment"}
      buttonBackgroundColor={secondaryColor}
      onClick={() => setIsEdit(true)}
    />
  );

  const editAction = (
    <div style={{ display: "flex", alignItems: "center" }}>
      <RBButton
        text="Cancel"
        buttonType={RBButtonType.cancel}
        style={{ marginRight: Spacing.medium }}
        onClick={() => {
          if (!isSaving) {
            setSelectedPaymentMethods(initialSelectedPaymentMethods);
            setIsRBPaySelected(initialRBPaySelected);
            setIsEdit(false);
          }
        }}
      />
      <RBButton
        text="Save"
        buttonBackgroundColor={secondaryColor}
        onClick={handleSave}
        isLoading={isSaving}
        disabled={isSaving || !hasSelectionChanged()}
      />
    </div>
  );

  const networkPaidCloseAction = (
    <div style={{ display: "flex", alignItems: "center" }}>
      <RBButton
        text="Close"
        buttonType={RBButtonType.cancel}
        style={{ marginRight: Spacing.medium }}
        onClick={() => {
          if (!isSaving) {
            // Revert any staged (unsaved) RB Pay toggle before collapsing.
            setIsRBPaySelected(initialRBPaySelected);
            setIsEdit(false);
          }
        }}
      />
      {/* Always show Save (disabled until there's a change), matching the editable
          flow. The network-configured methods stay read-only, so the only thing
          that can make this dirty is the worker's own RB Pay opt-in. */}
      <RBButton
        text="Save"
        buttonBackgroundColor={secondaryColor}
        onClick={handleSave}
        isLoading={isSaving}
        disabled={isSaving || !hasSelectionChanged()}
      />
    </div>
  );

  // Network paid mode: read-only expanded view with Back button (mirrors mobile)
  const networkPaidViewDetail = renderTextWithTitleAndChildComponent(
    "",
    <div style={{ width: "100%" }}>
      <div style={{ color: Colors.darkGrey, marginBottom: Spacing.small }}>
        {getDefaultMethodsTitle(true)}
      </div>
      {directPaymentMethods.length > 0 ? (
        <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: 20 }}>
          {directPaymentMethods.map((method) => (
            <li key={method} style={{ color: Colors.black, marginBottom: 2 }}>
              {formatPaymentMethodLabel(method)}
            </li>
          ))}
        </ul>
      ) : (
        <div style={{ color: Colors.darkGrey }}>No payment methods configured.</div>
      )}

      {shouldShowRBPay ? (
        <div style={{ marginTop: Spacing.large }}>
          {/* Same "Method / Enabled" table header as the editable (dental) view. */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              color: Colors.darkGrey,
              fontWeight: "bold",
              marginBottom: Spacing.small,
            }}
          >
            <div>Method</div>
            <div>Enabled</div>
          </div>
          <Divider style={{ marginBottom: Spacing.small }} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: Spacing.medium,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: "bold", fontSize: FontSize.large }}>{renderRBPayText(getRBPayDisplayName())}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: Spacing.smallest }}>
                {getRBPayStatusLabel() ? (
                  <div
                    style={{
                      color: getRBPayStatus() === "active" ? Colors.themeGreen : "#ef7a2f",
                      fontWeight: "bold",
                      fontSize: FontSize.default,
                    }}
                  >
                    {getRBPayStatusLabel()}
                  </div>
                ) : null}
                <RBButton
                  text={`Open ${getRBPayDisplayName()}`}
                  onClick={openRBPaySettings}
                  buttonBackgroundColor={secondaryColor}
                  disabled={isRBPayLoading}
                />
              </div>
              {!canEnableRBPay() && !isRBPayEnabled() ? (
                <div
                  style={{
                    marginTop: Spacing.smallest,
                    color: Colors.darkGrey,
                    fontSize: FontSize.default,
                    fontStyle: "italic",
                  }}
                >
                  {renderRBPayText(getRBPayHelperText())}
                </div>
              ) : null}
            </div>
            <Switch
              checked={isRBPayEnabled()}
              onChange={onToggleRBPay}
              color="primary"
              disabled={isRBPayLoading || (!isRBPayEnabled() && !canEnableRBPay())}
            />
          </div>
        </div>
      ) : null}
    </div>
  );

  // Determine which detail view to show based on mode
  const getDetailView = () => {
    if (!isEdit) {
      return viewDetail;
    }
    // In edit/view mode
    if (isNetworkPaid) {
      return networkPaidViewDetail;
    }
    return editDetail;
  };

  return (
    <>
      {/* Stripe How-To Modal - only show when not network paid */}
      {!isNetworkPaid && (
        <RBModal
          open={showStripeHowTo}
          onClose={() => setShowStripeHowTo(false)}
          backDropEnabled={true}
          modalTitle="Stripe Setup"
          style={{ minHeight: 0 }}
          children={
            <div style={{ padding: Spacing.large }}>
              <div style={{ color: Colors.darkGrey, marginBottom: Spacing.small }}>
                Your chosen bank is used for payouts. Payment may take up to 7 days to reach you.
              </div>
              <div style={{ color: Colors.darkGrey, marginBottom: Spacing.small }}>
                You may be redirected to Stripe in a new tab, Keep this screen open and return once
                complete.
              </div>
              <div style={{ color: Colors.darkGrey, marginBottom: Spacing.small }}>
                1. Open Stripe from the link.
              </div>
              <div style={{ color: Colors.darkGrey, marginBottom: Spacing.small }}>
                2. Enter your personal details
              </div>
              <div style={{ color: Colors.darkGrey, marginBottom: Spacing.small }}>
               3. Stripe requires a website for you to connect your account. Alternatively you can use a social media account, full URL not just your handle.
              </div>
              <div style={{ color: Colors.darkGrey, marginBottom: Spacing.small }}>
                4. Enter your banking information.
              </div>
              <div style={{ color: Colors.darkGrey, marginBottom: Spacing.small }}>
                5. Once successfully submitted, you will be directed to close the window and return to the Relief Buddy Application.
              </div>
              <div style={{ color: Colors.darkGrey, marginBottom: Spacing.medium }}>
                Note: If debit card is not compatible, then please enter your direct deposit banking
                details. All support for Stripe is available with your Stripe Account.
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: Spacing.medium }}>
                <RBButton
                  text={isStripeSetupDone() ? "Open Dashboard" : "Open Stripe"}
                  onClick={() => openStripeDestination(true)}
                  buttonBackgroundColor={secondaryColor}
                  isLoading={isOpeningStripe}
                  disabled={isOpeningStripe}
                />
                <RBButton
                  text="Close"
                  onClick={() => setShowStripeHowTo(false)}
                  buttonBackgroundColor={secondaryColor}
                />
              </div>
            </div>
          }
        />
      )}

      <ProfileCardSection
        sectionHeader="Acceptable Payment Methods"
        detail={getDetailView()}
        action={isEdit ? (isNetworkPaid ? networkPaidCloseAction : editAction) : action}
      />

      {/* Alert Modal */}
      <RBAlert
        show={alertVisible}
        alertTitle="Notice"
        alertMessage={alertMessage}
        buttons={[
          <RBButton
            key="ok"
            text="OK"
            onClick={() => setAlertVisible(false)}
            buttonBackgroundColor={secondaryColor}
          />,
        ]}
      />
    </>
  );
};
